import React, { CSSProperties, memo } from 'react';
import { alpha, Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const BASE_WRAPPER_STYLES: CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
} as const;

type SpinnerColorTypes =
  | 'inherit'
  | 'error'
  | 'info'
  | 'primary'
  | 'success'
  | 'secondary'
  | 'warning';

type modeTypes = 'replace' | 'overlay';

interface CircularProgressProps {
  loading: boolean;
  spinnerSize?: string | number;
  spinnerColor?: SpinnerColorTypes;
  preloaderWrapperStyles?: CSSProperties;
  children: React.ReactElement;
  mode?: modeTypes;
}

const CircularPreloader = memo<CircularProgressProps>((props) => {
  const {
    spinnerSize = 50,
    spinnerColor = 'primary',
    loading,
    preloaderWrapperStyles,
    children,
    mode = 'replace',
  } = props;
  const theme = useTheme();

  if (mode === 'replace') {
    return loading ? (
      <Box sx={{ ...BASE_WRAPPER_STYLES, ...preloaderWrapperStyles }}>
        <CircularProgress size={spinnerSize} color={spinnerColor} />
      </Box>
    ) : (
      children
    );
  }

  if (mode === 'overlay') {
    return (
      <div
        style={{
          position: loading ? 'relative' : 'static',
          width: '100%',
          height: '100%',
          backgroundColor: 'var(--palette-background-default)',
        }}
      >
        {children}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              backdropFilter: 'blur(1px)',
              zIndex: 10,
              transition: 'opacity 0.2s ease-in-out',
              ...preloaderWrapperStyles,
            }}
          >
            <CircularProgress size={spinnerSize} color={spinnerColor} />
          </Box>
        )}
      </div>
    );
  }
});

export default CircularPreloader;